import React, { useEffect, useState } from "react";

import { graphql, useStaticQuery } from "gatsby";
import PropTypes from "prop-types";

import PageLoadingOverlay from "components/Page/PageLoadingOverlay";

import HotspotRenderer from "./HotspotRenderer";

/**
 * Build the hotspots and place them in the hotspotContainer.
 *
 * @param hotspots a list of hotspot configurations.
 * @param hotspotContainer a `Marzipano.HotspotContainer` instance.
 *
 * Hotspots are sourced from hidden DOM elements using unique ids. An element
 * with the given `elementId` is expected to exist and will be used for the content
 * of the hotspot pop-up. These elements are expected to be self contained.
 *
 * A `Hotspot` container exists for rendering simple hotspots with a trigger icon.
 */
const addHotspots = (hotspots, hotspotContainer) => {
  hotspots.forEach((config) => {
    const hotspotSource = document.getElementById(config.elementId);
    // Clear the classes from the source wrapper otherwise it breaks formatting
    // when loaded into the Canvas.
    hotspotSource.className = "";
    hotspotContainer.createHotspot(hotspotSource, config.position);
  });
};

/**
 * Configure the scene and add the hotspots to the panorama viewer.
 *
 * @param Marzipano the Marzipano library. Importing this as a module fails during
 *   the Gatsby build process, requiring it to be provided using `require` on mount.
 * @param geometries the geometries available to the `CubeGeometry`.
 * @param maxResolution the max resolution for the view limiter.
 * @param maxVFov the max vertical field of view for the view limiter.
 * @param maxHFov the max horizontal field of view for the view limiter.
 * @param viewParams the parameters for the `RectilinearView`.
 * @param hotspots the list of all hotspot configurations.
 * @param previewImageSrc string path to the panorama image.
 * @param viewer the panorama viewer instance.
 * @returns {Scene}
 */
const createScene = (
  Marzipano,
  geometries,
  maxResolution,
  maxVFov,
  maxHFov,
  viewParams,
  hotspots,
  previewImageSrc,
  viewer
) => {
  const source = Marzipano.ImageUrlSource.fromString(
    "/tiles/{z}/{f}/{y}/{x}.jpg",
    { cubeMapPreviewUrl: previewImageSrc }
  );
  const geometry = new Marzipano.CubeGeometry(geometries);
  const limiter = Marzipano.RectilinearView.limit.traditional(
    maxResolution,
    maxVFov,
    maxHFov
  );
  const view = new Marzipano.RectilinearView(viewParams, limiter);

  // The hotspot container constructs and displays all hotspots in the viewer.
  const hotspotContainer = new Marzipano.HotspotContainer(
    viewer.domElement(),
    viewer.stage(),
    view,
    viewer.renderLoop()
  );
  // Add the hotspots to the container.
  addHotspots(hotspots, hotspotContainer);

  return viewer.createScene({
    source,
    geometry,
    view,
    pinFirstLevel: true,
  });
};

/**
 * Print the yaw and pitch coordinates for a click event's position on the panorama.
 *
 * Used in debug mode to determine the location of info spots.
 * @param panoramaViewer the panorama viewer object which converts the x/y
 *   coords to the pitch and yaw.
 * @param event
 */
const printClickCoordinates = (panoramaViewer, event) => {
  if (panoramaViewer) {
    console.debug(
      panoramaViewer
        .view()
        .screenToCoordinates({ x: event.clientX, y: event.clientY })
    );
  } else {
    console.debug("No viewer has loaded yet.");
  }
};

const VirtualConsultationRoom = (props) => {
  const data = useStaticQuery(graphql`
    query {
      previewImage: file(
        relativePath: { eq: "virtual-consultation-room/preview.jpg" }
      ) {
        childImageSharp {
          fluid {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  const [hasLoaded, setHasLoaded] = useState(false);
  const [panoramaViewer, setPanoramaViewer] = useState(null);

  const panoramaId = `virtual-consultation-room-${props.name}`;
  const previewImageSrc = data.previewImage.childImageSharp.fluid.src;

  useEffect(() => {
    if (!hasLoaded && typeof window !== "undefined") {
      // Only mount the panorama in production when the window object exists, to
      // avoid issues during Gatsby's build step.
      const Marzipano = require("marzipano");

      // Create the viewer and add it to the state for debug purposes.
      const viewer = new Marzipano.Viewer(document.getElementById(panoramaId));
      props.debug && setPanoramaViewer(viewer);

      // Create and load the scene.
      const scene = createScene(
        Marzipano,
        props.geometries,
        props.limiter.maxResolution,
        props.limiter.maxVFov,
        props.limiter.maxHFov,
        props.viewParams,
        props.hotspots,
        previewImageSrc,
        viewer
      );
      scene.switchTo();

      setHasLoaded(true);
    }
  });

  return (
    <>
      <PageLoadingOverlay loading={!hasLoaded} />
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions*/}
      <div
        id={panoramaId}
        className="virtual-consultation-room-container"
        onClick={
          props.debug
            ? (event) => printClickCoordinates(panoramaViewer, event)
            : undefined
        }
      />
      <HotspotRenderer />
      <span className="virtual-consultation-room-disclaimer small">
        Airport terminal is shown for illustration purposes only.
      </span>
    </>
  );
};

VirtualConsultationRoom.propTypes = {
  debug: PropTypes.bool,
  geometries: PropTypes.arrayOf(PropTypes.object).isRequired,
  limiter: PropTypes.shape({
    maxResolution: PropTypes.number.isRequired,
    maxVFov: PropTypes.number.isRequired,
    maxHFov: PropTypes.number.isRequired,
  }).isRequired,
  viewParams: PropTypes.object,
  hotspots: PropTypes.arrayOf(
    PropTypes.shape({
      elementId: PropTypes.string.isRequired,
      position: PropTypes.shape({
        pitch: PropTypes.number.isRequired,
        yaw: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired
  ),
};

VirtualConsultationRoom.defaultProps = {
  debug: false,
};

export default VirtualConsultationRoom;
